import React, { useEffect, useState, useRef } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon, QuoteIcon } from 'lucide-react';
interface Testimonial {
  quote: string;
  author: string;
  role: string;
  company: string;
  companyInitial: string;
  companyColor: string;
}
const testimonials: Testimonial[] = [{
  quote: 'ESCA закрили 12 вакансій за місяць — від менеджерів з продажу до фінансового директора. Якість кандидатів перевершила очікування.',
  author: 'Анна Коваленко',
  role: 'HR Director',
  company: 'TechCorp',
  companyInitial: 'T',
  companyColor: 'bg-blue-500'
}, {
  quote: 'Працюємо з агентством вже 3 роки. Цінуємо швидкість, прозорість і те, що рекрутери дійсно розуміють нашу індустрію.',
  author: 'Дмитро Петров',
  role: 'CEO',
  company: 'FinGroup',
  companyInitial: 'F',
  companyColor: 'bg-emerald-500'
}, {
  quote: 'Завдяки ESCA ми зібрали команду розробки з нуля за 2 місяці. Усі 8 спеціалістів пройшли випробувальний термін.',
  author: 'Олена Сидоренко',
  role: 'CTO',
  company: 'DataFlow',
  companyInitial: 'D',
  companyColor: 'bg-violet-500'
}, {
  quote: 'Чудова робота з C‑level позиціями. Знайшли нам COO, який за півроку збільшив операційну ефективність на 30%.',
  author: 'Максим Іванов',
  role: 'Засновник',
  company: 'ScaleUp',
  companyInitial: 'S',
  companyColor: 'bg-amber-500'
}];
const clientLogos = [{
  name: 'TechCorp',
  initial: 'TC',
  color: 'bg-blue-100 text-blue-600'
}, {
  name: 'FinGroup',
  initial: 'FG',
  color: 'bg-emerald-100 text-emerald-600'
}, {
  name: 'DataFlow',
  initial: 'DF',
  color: 'bg-violet-100 text-violet-600'
}, {
  name: 'ScaleUp',
  initial: 'SU',
  color: 'bg-amber-100 text-amber-600'
}, {
  name: 'MedTech',
  initial: 'MT',
  color: 'bg-rose-100 text-rose-600'
}, {
  name: 'LogiPro',
  initial: 'LP',
  color: 'bg-cyan-100 text-cyan-600'
}, {
  name: 'RetailX',
  initial: 'RX',
  color: 'bg-orange-100 text-orange-600'
}, {
  name: 'BuildCo',
  initial: 'BC',
  color: 'bg-teal-100 text-teal-600'
}];
export function TestimonialsSection() {
  const [current, setCurrent] = useState(0);
  const [direction, setDirection] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  const next = () => {
    setDirection(1);
    setCurrent(prev => (prev + 1) % testimonials.length);
  };
  const prev = () => {
    setDirection(-1);
    setCurrent(prev => (prev - 1 + testimonials.length) % testimonials.length);
  };
  useEffect(() => {
    const timer = setInterval(next, 6000);
    return () => clearInterval(timer);
  }, []);
  const testimonial = testimonials[current];
  const slideVariants = {
    enter: (dir: number) => ({
      x: dir > 0 ? 80 : -80,
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1
    },
    exit: (dir: number) => ({
      x: dir > 0 ? -80 : 80,
      opacity: 0
    })
  };
  return <section className="py-20 lg:py-28 bg-white" data-id="element-431">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" ref={ref} data-id="element-432">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5
      }} className="text-center mb-14" data-id="element-433">
          <span className="text-sm font-semibold text-primary-600 tracking-wide uppercase" data-id="element-434">
            Відгуки
          </span>
          <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-navy-900 tracking-tight" data-id="element-435">
            Нам довіряють
          </h2>
        </motion.div>

        {/* Client logos */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5,
        delay: 0.1
      }} className="flex flex-wrap justify-center gap-4 mb-14" data-id="element-436">
          {clientLogos.map(logo => <div key={logo.name} className={`w-16 h-16 rounded-xl ${logo.color} flex items-center justify-center text-sm font-bold`} title={logo.name} data-id="element-437">
              {logo.initial}
            </div>)}
        </motion.div>

        {/* Testimonial Carousel */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5,
        delay: 0.2
      }} className="max-w-3xl mx-auto" data-id="element-438">
          <div className="relative bg-navy-50/60 rounded-3xl p-8 sm:p-12 border border-navy-100" data-id="element-439">
            <QuoteIcon className="w-10 h-10 text-primary-200 mb-6" data-id="element-440" />

            <div className="min-h-[160px] flex items-center" data-id="element-441">
              <AnimatePresence mode="wait" custom={direction} data-id="element-442">
                <motion.div key={current} custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
                duration: 0.35
              }} data-id="element-443">
                  <blockquote className="text-lg sm:text-xl text-navy-700 leading-relaxed font-medium" data-id="element-444">
                    «{testimonial.quote}»
                  </blockquote>
                  <div className="mt-8 flex items-center gap-4" data-id="element-445">
                    <div className={`w-12 h-12 rounded-full ${testimonial.companyColor} flex items-center justify-center text-white font-bold text-sm`} data-id="element-446">
                      {testimonial.companyInitial}
                    </div>
                    <div data-id="element-447">
                      <div className="font-bold text-navy-900" data-id="element-448">
                        {testimonial.author}
                      </div>
                      <div className="text-sm text-navy-500" data-id="element-449">
                        {testimonial.role}, {testimonial.company}
                      </div>
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Navigation */}
            <div className="flex items-center justify-between mt-8" data-id="element-450">
              <div className="flex gap-2" data-id="element-451">
                {testimonials.map((_, i) => <button key={i} onClick={() => {
                setDirection(i > current ? 1 : -1);
                setCurrent(i);
              }} className={`w-2.5 h-2.5 rounded-full transition-all ${i === current ? 'bg-primary-500 w-8' : 'bg-navy-300'}`} aria-label={`Відгук ${i + 1}`} data-id="element-452" />)}
              </div>
              <div className="flex gap-2" data-id="element-453">
                <button onClick={prev} className="w-10 h-10 rounded-xl border border-navy-200 flex items-center justify-center text-navy-500 hover:bg-navy-100 transition-colors" aria-label="Попередній відгук" data-id="element-454">
                  <ChevronLeftIcon className="w-5 h-5" data-id="element-455" />
                </button>
                <button onClick={next} className="w-10 h-10 rounded-xl border border-navy-200 flex items-center justify-center text-navy-500 hover:bg-navy-100 transition-colors" aria-label="Наступний відгук" data-id="element-456">
                  <ChevronRightIcon className="w-5 h-5" data-id="element-457" />
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}